import { lazy, type ComponentType } from 'react';

/**
 * Wrap React.lazy with auto-recovery for stale chunk errors after a deploy.
 *
 * Symptom we're guarding against: a user keeps a tab open across a deploy. Vite
 * regenerates chunks with new content hashes, the old `index.html` still cached
 * by the SW (or browser) references chunks that no longer exist on the server,
 * and the dynamic import fails with:
 *   "Failed to fetch dynamically imported module: …/assets/Foo-<hash>.js"
 *
 * Strategy: catch the error once, force a hard reload to fetch the fresh
 * `index.html` + chunk graph. We mark the attempt in sessionStorage so a
 * genuinely missing chunk (config error) doesn't cause an infinite reload
 * loop — the second failure bubbles up to the ErrorBoundary as normal.
 */
const RELOAD_FLAG = 'pm-chunk-reload-attempted';

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = (error as { message?: string }).message ?? String(error);
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // Return a never-resolving promise to keep Suspense quiet until the
        // page actually reloads. Anything else briefly mounts an error.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

/**
 * Clear the reload flag once any lazy chunk loads successfully — i.e. once we
 * know the user is on a healthy build. Call this from the app shell on mount
 * so a future stale-chunk event in the same tab can re-trigger the recovery.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG);
}
